import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/axios';
import { confirmDelete } from '../utils/confirmDelete';
import { T, PageHeader, Alert } from '../components/UI';

export default function SiteLocations() {
  const navigate = useNavigate();
  const [items,    setItems]    = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [error,    setError]    = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editing,  setEditing]  = useState(null);
  const [form,     setForm]     = useState({ name:'', address:'' });
  const [saving,   setSaving]   = useState(false);
  const [deleting, setDeleting] = useState(null);

  const fetch = async () => {
    setLoading(true);
    try { const { data } = await api.get('/users/site-locations'); setItems(data.locations||[]); }
    catch { setError('Failed to load'); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetch(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    setSaving(true); setError(null);
    try {
      if (editing) { await api.patch(`/users/site-locations/${editing._id}`, form); }
      else { await api.post('/users/site-locations', form); }
      setForm({ name:'', address:'' }); setShowForm(false); setEditing(null); fetch();
    } catch (err) { setError(err.response?.data?.message || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const startEdit = (item) => { setEditing(item); setForm({ name:item.name||'', address:item.address||'' }); setShowForm(true); window.scrollTo({top:0,behavior:'smooth'}); };
  const cancelForm = () => { setShowForm(false); setEditing(null); setForm({ name:'', address:'' }); };
  const handleDelete = async (id, name) => {
    if (!window.confirm(`Remove "${name}"?`)) return;
    if (!await confirmDelete()) return;
    setDeleting(id);
    try { await api.delete(`/users/site-locations/${id}`); fetch(); }
    catch { setError('Failed to remove'); }
    finally { setDeleting(null); }
  };

  return (
    <div style={T.page}>
      <PageHeader title="Inspection Location" onBack={()=>navigate(-1)} actions={
        <button onClick={()=>{ cancelForm(); setShowForm(s=>!s); }} style={T.btnPrimary}><i className="ti ti-plus" aria-hidden="true"/> Add</button>
      }/>
      <div style={{ maxWidth:'600px', margin:'0 auto', padding:'16px 16px 80px' }}>
        {error && <Alert type="error">{error}</Alert>}
        {showForm && (
          <div style={{ background:'var(--surface)', border:`1px solid ${editing?'var(--amber)':'var(--blue)'}`, borderRadius:'var(--radius-lg)', padding:'16px', marginBottom:'16px' }}>
            <div style={{ fontSize:'13px', fontWeight:'500', color:editing?'var(--amber)':'var(--blue)', marginBottom:'12px' }}>{editing?`Edit: ${editing.name}`:'Add New Inspection Location'}</div>
            <form onSubmit={handleSubmit}>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px', marginBottom:'12px' }}>
                <div>
                  <label style={T.label}>Site Name *</label>
                  <input value={form.name} onChange={e=>setForm(p=>({...p,name:e.target.value}))} style={T.input} placeholder="e.g. Hertz Welshpool" required onFocus={e=>e.target.style.borderColor='var(--blue)'} onBlur={e=>e.target.style.borderColor='var(--border-md)'}/>
                </div>
                <div>
                  <label style={T.label}>Address</label>
                  <input value={form.address} onChange={e=>setForm(p=>({...p,address:e.target.value}))} style={T.input} placeholder="123 Street, Perth WA" onFocus={e=>e.target.style.borderColor='var(--blue)'} onBlur={e=>e.target.style.borderColor='var(--border-md)'}/>
                </div>
              </div>
              <div style={{ display:'flex', gap:'8px', justifyContent:'flex-end' }}>
                <button type="button" onClick={cancelForm} style={T.btnSecondary}>Cancel</button>
                <button type="submit" disabled={saving} style={{ ...T.btnPrimary, background:editing?'var(--amber)':'var(--blue)', opacity:saving?0.6:1 }}>{saving?'Saving...':editing?'Save Changes':'Add Inspection Location'}</button>
              </div>
            </form>
          </div>
        )}
        {loading ? <div style={{ textAlign:'center', padding:'40px', color:'var(--muted)' }}>Loading...</div> : (
          <div style={{ display:'flex', flexDirection:'column', gap:'8px' }}>
            {items.map(item=>(
              <div key={item._id} style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:'var(--radius-lg)', padding:'14px 16px', display:'flex', alignItems:'center', gap:'12px' }}>
                <div style={{ width:'36px', height:'36px', background:'var(--blue-light)', borderRadius:'var(--radius)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                  <i className="ti ti-map" style={{ color:'var(--blue)', fontSize:'16px' }} aria-hidden="true"/>
                </div>
                <div style={{ flex:1 }}>
                  <div style={{ fontWeight:'500', color:'var(--text)', fontSize:'13px' }}>{item.name}</div>
                  {item.address && <div style={{ fontSize:'12px', color:'var(--muted)' }}>{item.address}</div>}
                </div>
                <div style={{ display:'flex', gap:'6px' }}>
                  <button onClick={()=>startEdit(item)} style={{ ...T.btnIcon, color:'var(--blue)', borderColor:'rgba(10,110,180,0.25)' }}><i className="ti ti-edit" style={{ fontSize:'14px' }} aria-label="Edit"/></button>
                  <button onClick={()=>handleDelete(item._id, item.name)} disabled={deleting===item._id} style={{ ...T.btnIcon, color:'var(--red)', borderColor:'rgba(163,45,45,0.25)' }}><i className="ti ti-trash" style={{ fontSize:'14px' }} aria-label="Delete"/></button>
                </div>
              </div>
            ))}
            {items.length===0 && <div style={{ textAlign:'center', padding:'40px', color:'var(--muted)', background:'var(--surface)', border:'1px solid var(--border)', borderRadius:'var(--radius-lg)' }}>No inspection locations yet.</div>}
          </div>
        )}
      </div>
    </div>
  );
}
